import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../store/useAuthStore';
import { usePlanningStore } from '../../store/usePlanningStore';
import {
  getActiveDay,
  getClockStateOn,
  hasMissingClockOut,
  useTimeclockStore,
} from '../../store/useTimeclockStore';
import { useAbsenceStore } from '../../store/useAbsenceStore';
import { useOpenItemsStore } from '../../store/useOpenItemsStore';
import {
  toOpenDayGroups,
  useMyChecklistStore,
  type TaskChecklistGroup,
} from '../../store/useMyChecklistStore';
import { useClockOutGuard } from '../../hooks/useClockOutGuard';
import { useClockStamp } from '@/hooks/useClockStamp';
import { getTasks } from '../../api/generated/tasks/tasks';
import type { TaskDto } from '../../types';

import { NowHero } from '../../components/Dashboard/Employee/NowHero';
import { DayTimelineStrip } from '../../components/Dashboard/Employee/DayTimelineStrip';
import { UpNextCard } from '../../components/Dashboard/Employee/UpNextCard';
import {
  MaterialsCard,
  type MaterialItem as DashboardMaterialItem,
} from '../../components/Dashboard/Employee/MaterialsCard';
import { QuickActionsCard } from '../../components/Dashboard/Employee/QuickActionsCard';
import { OpenItemsCard } from '../../components/Dashboard/OpenItemsCard';
import { ClockStampDialogs } from '../../components/Dashboard/Employee/ClockStampDialogs';
import { OpenDaysNotice } from '../../components/Dashboard/Employee/OpenDaysNotice';

import {
  getMondayOfWeek,
  nowMinutes as nowMinutesFn,
  getCurrentTask,
  getUpcomingTasks,
  getDoneTasks,
  getDayTotals,
} from '../../utils/dashboardData';
import { isoDate } from '../../utils/dateUtils';
import { displayDayMinutes } from '../../utils/timeclockTotals';
import { useClockInWindow } from '../../hooks/useClockInWindow';
import { useDescribeVehicle } from '../../hooks/useDescribeVehicle';

const tasksApi = getTasks();

export function EmployeeDashboard() {
  const navigate = useNavigate();
  const { employee } = useAuthStore();
  const { entries, loading: planningLoading, fetchWeekEntries } = usePlanningStore();
  const {
    todayEntry,
    openDays,
    loading: timeclockLoading,
    fetchToday,
    fetchOpenDays,
    recordEvent,
  } = useTimeclockStore();
  const { absences, fetchAbsences } = useAbsenceStore();
  const fetchChecklistDay = useMyChecklistStore((s) => s.fetchDay);
  const checklistDays = useMyChecklistStore((s) => s.days);
  const fetchOpenPastChecklists = useMyChecklistStore((s) => s.fetchOpenPast);
  const openPastChecklists = useMyChecklistStore((s) => s.openPast);
  const myOpenItems = useOpenItemsStore((s) => s.myItems);
  const refreshMyOpenItems = useOpenItemsStore((s) => s.refreshMyCount);

  const [now, setNow] = useState(() => new Date());
  const nowMin = nowMinutesFn(now);
  const { allowed: clockInAllowed } = useClockInWindow();
  const [taskDetails, setTaskDetails] = useState<Record<string, TaskDto>>({});

  useEffect(() => {
    const id = setInterval(() => {
      setNow(new Date());
    }, 60_000);
    return () => clearInterval(id);
  }, []);

  const today = useMemo(() => new Date(), []);
  const todayIso = useMemo(() => isoDate(today), [today]);
  const weekRange = useMemo(() => {
    const monday = getMondayOfWeek(today);
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);
    return { start: isoDate(monday), end: isoDate(sunday) };
  }, [today]);

  useEffect(() => {
    if (!employee?.id) return;
    fetchWeekEntries(weekRange.start, weekRange.end, employee.id);
    fetchToday();
    fetchOpenDays();
    fetchAbsences({ employeeId: employee.id });
    fetchChecklistDay(todayIso);
    fetchOpenPastChecklists();
    refreshMyOpenItems();
  }, [
    employee?.id,
    weekRange.start,
    weekRange.end,
    todayIso,
    fetchWeekEntries,
    fetchToday,
    fetchOpenDays,
    fetchAbsences,
    fetchChecklistDay,
    fetchOpenPastChecklists,
    refreshMyOpenItems,
  ]);

  const checklistDay = checklistDays[todayIso];

  const checklistsByTaskId = useMemo(() => {
    const map: Record<string, TaskChecklistGroup> = {};
    (checklistDay?.tasks ?? []).forEach((group) => {
      if (group.taskId) map[group.taskId] = group;
    });
    return map;
  }, [checklistDay]);

  // Counted from the deduped past groups, not from the raw payload — server and browser can
  // disagree about "today" around midnight, and the same day must not be counted twice.
  const openPastChecklistCount = useMemo(
    () =>
      toOpenDayGroups(openPastChecklists, todayIso).reduce(
        (sum, group) =>
          sum +
          group.tasks.reduce(
            (taskSum, task) => taskSum + (task.items ?? []).filter((i) => !i.isCompleted).length,
            0,
          ),
        0,
      ),
    [openPastChecklists, todayIso],
  );

  const openChecklistCount = useMemo(
    () =>
      (checklistDay?.tasks ?? []).reduce(
        (sum, group) => sum + (group.items ?? []).filter((i) => !i.isCompleted).length,
        0,
      ) + openPastChecklistCount,
    [checklistDay, openPastChecklistCount],
  );

  const todayPlan = useMemo(
    () => entries.find((e) => e.date === todayIso && e.entryType !== 'Absence') ?? null,
    [entries, todayIso],
  );

  const todayTasks = useMemo(
    () =>
      (todayPlan?.tasks ?? []).slice().sort((a, b) => {
        const ax = a.startTime ?? '99:99';
        const bx = b.startTime ?? '99:99';
        return ax.localeCompare(bx);
      }),
    [todayPlan],
  );

  // Objects of today's assignments, offered first when reporting something from the dashboard.
  const todayObjects = useMemo(() => {
    const byId = new Map<string, string>();
    todayTasks.forEach((task) => {
      if (task.objectId && !byId.has(task.objectId)) byId.set(task.objectId, task.objectName ?? '');
    });
    return Array.from(byId, ([id, label]) => ({ id, label }));
  }, [todayTasks]);

  const currentTask = useMemo(() => getCurrentTask(todayTasks, nowMin), [todayTasks, nowMin]);
  const upcomingTasks = useMemo(() => getUpcomingTasks(todayTasks, nowMin), [todayTasks, nowMin]);
  const doneTasks = useMemo(() => getDoneTasks(todayTasks, nowMin), [todayTasks, nowMin]);
  const dayTotals = useMemo(() => getDayTotals(todayPlan, nowMin), [todayPlan, nowMin]);

  const onAbsence = useMemo(() => {
    const todays = entries.find((e) => e.date === todayIso && e.entryType === 'Absence');
    return Boolean(todays);
  }, [entries, todayIso]);
  const absenceLabel = useMemo(() => {
    if (!onAbsence) return undefined;
    const a = absences.find(
      (x) => x.startDate && x.endDate && x.startDate <= todayIso && x.endDate >= todayIso,
    );
    return a?.absenceTypeName ?? undefined;
  }, [absences, todayIso, onAbsence]);

  const todayTaskIds = useMemo(() => {
    const ids = new Set<string>();
    todayTasks.forEach((t) => {
      if (t.taskId) ids.add(t.taskId);
    });
    return Array.from(ids);
  }, [todayTasks]);

  const loadTaskDetails = useCallback(async () => {
    const missing = todayTaskIds.filter((id) => !taskDetails[id]);
    if (missing.length === 0) return;
    const next: Record<string, TaskDto> = {};
    await Promise.allSettled(
      missing.map(async (taskId) => {
        try {
          const data = await tasksApi.getApiV1TasksId(taskId);
          if (data) {
            next[taskId] = {
              taskId: data.taskId ?? '',
              tenantId: data.tenantId ?? '',
              serviceId: data.serviceId ?? '',
              serviceName: data.serviceName ?? '',
              objectId: data.objectId ?? '',
              objectName: data.objectName ?? '',
              customerName: data.customerName ?? null,
              name: data.name ?? '',
              description: data.description ?? null,
              estimatedDurationMinutes: data.estimatedDurationMinutes ?? 0,
              materials: (data.materials as unknown as TaskDto['materials']) ?? null,
              instructions: data.instructions ?? null,
              isActive: data.isActive ?? true,
              isBlocking: data.isBlocking ?? false,
              createdAt: data.createdAt ?? '',
              updatedAt: data.updatedAt ?? null,
            };
          }
        } catch {
          // ignore individual failures
        }
      }),
    );
    if (Object.keys(next).length > 0) {
      setTaskDetails((prev) => ({ ...prev, ...next }));
    }
  }, [todayTaskIds, taskDetails]);

  useEffect(() => {
    loadTaskDetails();
  }, [loadTaskDetails]);

  const materials: DashboardMaterialItem[] = useMemo(() => {
    const list: DashboardMaterialItem[] = [];
    todayTasks.forEach((task) => {
      const detail = task.taskId ? taskDetails[task.taskId] : null;
      if (!detail?.materials) return;
      detail.materials.forEach((mat) => {
        list.push({
          taskId: task.taskId,
          objectId: task.objectId ?? detail.objectId,
          taskName: task.taskName ?? detail.name ?? '',
          objectName: task.objectName ?? detail.objectName ?? '',
          name: mat.name,
          quantity: mat.quantity ?? null,
        });
      });
    });
    return list;
  }, [todayTasks, taskDetails]);

  // A night shift past midnight stays on its own day until it is clocked out (FACF-149).
  const activeDay = getActiveDay(todayEntry, openDays, todayIso);
  const activeDate = activeDay.date;
  const timeclockState = getClockStateOn(activeDay.entry, activeDate);

  // Planned vehicle of the day (FACF-118); the running trip itself shows in the hero's trip menu.
  const describeVehicle = useDescribeVehicle();
  const plannedVehicle = todayPlan?.plannedDriverRole
    ? (describeVehicle({ vehicleId: todayPlan.plannedVehicleId, driverRole: todayPlan.plannedDriverRole }) ??
      todayPlan.plannedLicensePlate ??
      null)
    : null;
  // Stamped time, counted like the time clock page and running while clocked in (FACF-87).
  const workedMinutes = activeDay.entry ? displayDayMinutes(activeDay.entry, now).workMinutes : 0;
  const clockInBlocked = hasMissingClockOut(openDays, todayIso);

  // Success and (localized) error toasts come from the store via the header TimeclockWidget,
  // which is mounted on every page — toasting here as well would show each message twice.
  const record = useCallback(
    async (...args: Parameters<typeof recordEvent>) => {
      try {
        await recordEvent(...args);
      } catch {
        // Already surfaced by the store.
      }
    },
    [recordEvent],
  );

  // Clock-in and clock-out ask for the work location and the vehicle of the day first.
  const clockStamp = useClockStamp();
  const { stamp } = clockStamp;
  const stampWithQuestions = useCallback(
    async (...args: Parameters<typeof stamp>) => {
      try {
        await stamp(...args);
      } catch {
        // Already surfaced by the store.
      }
    },
    [stamp],
  );

  const handleClockIn = useCallback(() => stampWithQuestions('clockIn'), [stampWithQuestions]);
  const performClockOut = useCallback(
    () => stampWithQuestions('clockOut', activeDate),
    [stampWithQuestions, activeDate],
  );

  // Warns about unconfirmed checklist items before clocking out — never blocks.
  const clockOutGuard = useClockOutGuard(todayIso, performClockOut);

  const handleBreakStart = useCallback(() => record('breakStart', activeDate), [record, activeDate]);
  const handleBreakEnd = useCallback(() => record('breakEnd', activeDate), [record, activeDate]);

  if (planningLoading && entries.length === 0) {
    return <EmployeeDashboardSkeleton />;
  }

  return (
    <div className="space-y-4 sm:space-y-6 animate-fade-in">
      <OpenDaysNotice
        openDays={openDays}
        todayIso={todayIso}
        busy={timeclockLoading}
        onEditDay={(day) => navigate(`/timeclock?day=${day.date ?? ''}`)}
        onShowUnconfirmed={() => navigate('/timeclock')}
      />

      <NowHero
        currentTask={currentTask}
        upcomingFirst={upcomingTasks[0] ?? null}
        nowMinutes={nowMin}
        workedMinutes={workedMinutes}
        targetMinutes={dayTotals.targetMinutes}
        timeclockState={timeclockState}
        clockInAllowed={clockInAllowed}
        clockInBlocked={clockInBlocked}
        busy={timeclockLoading || clockOutGuard.checking || clockStamp.preparing}
        onAbsenceToday={onAbsence}
        absenceLabel={absenceLabel}
        openChecklistCount={openChecklistCount}
        openPastChecklistCount={openPastChecklistCount}
        vehicleLabel={plannedVehicle}
        onClockIn={handleClockIn}
        onClockOut={clockOutGuard.guardClockOut}
        onBreakStart={handleBreakStart}
        onBreakEnd={handleBreakEnd}
      />

      <DayTimelineStrip tasks={todayTasks} nowMinutes={nowMin} />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
        <div className="lg:col-span-2">
          <UpNextCard
            doneTasks={doneTasks}
            currentTask={currentTask}
            upcomingTasks={upcomingTasks}
            remainingMinutes={dayTotals.remainingMinutes}
            checklistsByTaskId={checklistsByTaskId}
            checklistDate={todayIso}
          />
        </div>
        <div className="space-y-4 sm:space-y-6">
          <OpenItemsCard items={myOpenItems} maxRows={4} />
          <MaterialsCard materials={materials} />
          <QuickActionsCard reportObjectSuggestions={todayObjects} />
        </div>
      </div>

      <ClockStampDialogs clockStamp={clockStamp} clockOutGuard={clockOutGuard} todayIso={todayIso} busy={timeclockLoading} />
    </div>
  );
}

function EmployeeDashboardSkeleton() {
  return (
    <div className="space-y-4 sm:space-y-6 animate-pulse" aria-hidden>
      <div className="h-44 rounded-2xl bg-surface-sunken dark:bg-slate-800" />
      <div className="h-24 rounded-xl bg-surface-sunken dark:bg-slate-800" />
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
        <div className="lg:col-span-2 h-72 rounded-xl bg-surface-sunken dark:bg-slate-800" />
        <div className="space-y-4 sm:space-y-6">
          <div className="h-40 rounded-xl bg-surface-sunken dark:bg-slate-800" />
          <div className="h-40 rounded-xl bg-surface-sunken dark:bg-slate-800" />
          <div className="h-40 rounded-xl bg-surface-sunken dark:bg-slate-800" />
        </div>
      </div>
    </div>
  );
}
